import React, { useContext, useMemo } from 'react';
import { getAnalyticsService } from 'services/analyticsService';

import { AppContext } from '../../../context/app';
import { type Place, ScreenSizes } from '../../../context/types';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useSetPlace } from '../../../hooks/useSetPlace';
import LocationButton from '../../shared/locationButton/LocationButton';
import styles from './sectionLocations.module.scss';

type SectionLocationsProps = {
  locations: Place[];
};

const SectionLocations: React.FC<SectionLocationsProps> = ({ locations }) => {
  const analyticsService = useMemo(() => getAnalyticsService(), []);
  const { selectedLocation, selectedSite } = useContext(AppContext);
  const { kiosk, kioskMode } = useKioskManager();
  const setPlace = useSetPlace();

  const campusLocations = useMemo(
    () => locations.filter((place) => place.location.site === selectedSite?.id),
    [locations, selectedSite?.id],
  );

  // Handle grid rows for locations
  const locationRows = useMemo(() => {
    const width = window.innerWidth;
    const numLocations = campusLocations.length;

    if (width > ScreenSizes.LARGE) return Math.ceil(numLocations / 3);
    if (width > ScreenSizes.SMALL) return Math.ceil(numLocations / 4);
    return Math.ceil(numLocations / 2);
  }, [campusLocations]);

  return (
    <section
      data-testid="section-locations"
      className={`
        ${styles.list} 
        ${styles.locations} 
        ${kiosk && kioskMode && styles[kiosk.model]}
      `}
      style={{
        gridTemplateRows: `repeat(${locationRows}, minmax(0, 1fr))`,
      }}
    >
      {campusLocations.map((place, resultSlot) => (
        <LocationButton
          key={place.id}
          place={place}
          onClick={(place) => {
            analyticsService.track(
              analyticsService.events.GlobalCampusSearchSelected,
              {
                destinationId: place.id,
                resultSlot,
              },
            );
            setPlace(place);
          }}
          active={selectedLocation?.id === place.id}
        />
      ))}
    </section>
  );
};

export default SectionLocations;
